/**
 * Pinned Cleo mark geometry from cleo-mark-one-instrument, C6 field-off cut.
 *
 * The field/census is deliberately absent. Runtime renderers may add the
 * slice at desk grain, but the membrane, gap, slice, and core geometry must
 * remain byte-for-byte traceable to the reviewed source assets.
 */
export const CLEO_MARK_VIEW_BOX = "0 0 512 512";
export const CLEO_MARK_MEMBRANE_PATH = "M 418.8 158.1 A 190 190 0 1 1 353.9 93.2";
export const CLEO_MARK_SLICE_PATH = "M256 256 L364.2 76 A210 210 0 0 1 436 147.8 Z";
export const CLEO_MARK_MEMBRANE_STROKE_WIDTH = 30;
export const CLEO_MARK_CORE = { cx: 256, cy: 256, r: 40 } as const;
// DATED 2026-09-02 (FD-CAN-37, the principal: "change (for now) Cleo's color
// to the neucleos orange which is roughly f84e19"): the pinned seal follows
// the contract's mark — TH3's fork guard is exactly the law that ONE Cleo
// ships, so the seal cannot keep the green while the interface turns orange.
// The geometry is untouched; only the identity color moved (#5BAD97 before).
// DATED 2026-09-14 (GOALS/GOALS-DESIGN.html#hig5, the principal: "adopt the
// most yellow hue that's in the mark"): the seal follows the contract's mark
// to #DA7210, the yellow end of the sweep, one step deeper than #EF8530.
export const CLEO_MARK_IDENTITY_COLOR = "#DA7210";
export const CLEO_MARK_FIELD_COLOR = "#0B0E0D";
export const CLEO_MARK_SLICE_END_OPACITY = 0.2;

/** Exact reviewed glance/seal glyph (the source intentionally has no terminal newline). */
export function renderCleoMarkGlyphSvg(): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${CLEO_MARK_VIEW_BOX}">
  <path d="${CLEO_MARK_MEMBRANE_PATH}" fill="none" stroke="${CLEO_MARK_IDENTITY_COLOR}" stroke-width="${CLEO_MARK_MEMBRANE_STROKE_WIDTH}" stroke-linecap="round"></path>
  <circle cx="${CLEO_MARK_CORE.cx}" cy="${CLEO_MARK_CORE.cy}" r="${CLEO_MARK_CORE.r}" fill="${CLEO_MARK_IDENTITY_COLOR}"></circle>
</svg>`;
}

/** Exact reviewed C6 avatar, including its terminal newline. */
export function renderCleoMarkAvatarSvg(): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${CLEO_MARK_VIEW_BOX}">
  <defs>
    <radialGradient id="slice" cx="50%" cy="50%" r="50%">
      <stop offset="0%" stop-color="${CLEO_MARK_IDENTITY_COLOR}" stop-opacity="0"></stop>
      <stop offset="100%" stop-color="${CLEO_MARK_IDENTITY_COLOR}" stop-opacity="${CLEO_MARK_SLICE_END_OPACITY}"></stop>
    </radialGradient>
  </defs>
  <circle cx="256" cy="256" r="256" fill="${CLEO_MARK_FIELD_COLOR}"></circle>
  <path d="${CLEO_MARK_SLICE_PATH}" fill="url(#slice)"></path>
  <path d="${CLEO_MARK_MEMBRANE_PATH}" fill="none" stroke="${CLEO_MARK_IDENTITY_COLOR}" stroke-width="${CLEO_MARK_MEMBRANE_STROKE_WIDTH}" stroke-linecap="round"></path>
  <circle cx="${CLEO_MARK_CORE.cx}" cy="${CLEO_MARK_CORE.cy}" r="${CLEO_MARK_CORE.r}" fill="${CLEO_MARK_IDENTITY_COLOR}"></circle>
</svg>
`;
}

/**
 * macOS app-icon canvas: Apple's 1024-pt template places the artwork on an
 * 824-pt rounded plate (corner radius 185) centred with a 100-pt margin; the
 * system masks and shadows it. The avatar's field is the plate, so the mark
 * reads as the membrane, gap, slice and core on a dark squircle.
 */
export const CLEO_MARK_MAC_ICON_CANVAS = 1024;
export const CLEO_MARK_MAC_ICON_PLATE = { x: 100, y: 100, size: 824, radius: 185 } as const;
export const CLEO_MARK_MAC_ICON_MARK = { x: 192, y: 192, size: 640 } as const;

export function renderCleoMarkMacIconSvg(): string {
  const plate = CLEO_MARK_MAC_ICON_PLATE;
  const mark = CLEO_MARK_MAC_ICON_MARK;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${CLEO_MARK_MAC_ICON_CANVAS} ${CLEO_MARK_MAC_ICON_CANVAS}">
  <defs>
    <radialGradient id="slice" cx="50%" cy="50%" r="50%">
      <stop offset="0%" stop-color="${CLEO_MARK_IDENTITY_COLOR}" stop-opacity="0"></stop>
      <stop offset="100%" stop-color="${CLEO_MARK_IDENTITY_COLOR}" stop-opacity="${CLEO_MARK_SLICE_END_OPACITY}"></stop>
    </radialGradient>
  </defs>
  <rect x="${plate.x}" y="${plate.y}" width="${plate.size}" height="${plate.size}" rx="${plate.radius}" fill="${CLEO_MARK_FIELD_COLOR}"></rect>
  <svg x="${mark.x}" y="${mark.y}" width="${mark.size}" height="${mark.size}" viewBox="${CLEO_MARK_VIEW_BOX}">
    <path d="${CLEO_MARK_SLICE_PATH}" fill="url(#slice)"></path>
    <path d="${CLEO_MARK_MEMBRANE_PATH}" fill="none" stroke="${CLEO_MARK_IDENTITY_COLOR}" stroke-width="${CLEO_MARK_MEMBRANE_STROKE_WIDTH}" stroke-linecap="round"></path>
    <circle cx="${CLEO_MARK_CORE.cx}" cy="${CLEO_MARK_CORE.cy}" r="${CLEO_MARK_CORE.r}" fill="${CLEO_MARK_IDENTITY_COLOR}"></circle>
  </svg>
</svg>
`;
}
